import React, { useEffect, useState, useRef } from "react";
import { Hooks, Util, Request, Context, Config } from "@xt/xtbase";
const { useTranslation } = Hooks;
const { moment, Big, getUrl, showRate } = Util;
const { XtSocketPublic } = Request;
const { XtContext } = Context;
import { get_kline, get_ticker24h, get_topConfig, get_cfdMarketTopConfig } from "@/api/v4/market";
import { useStore } from "@/models";
import { observer } from "mobx-react-lite";
import styles from "./index.module.less";
import LineChart from "@/components/App/LineChart";
import Star from "@/components/Pages/Market/MarketList/star";
import AiSelectNavButton from "@/components/App/AiSelectNavButton";
import { isAiselectEligibleSymbol } from "@/utils/aiSelectEligible";
// 首页 Favourite 与 /market 的 Favourite 完全一致：直接复用 Market 列表
import MarketList from "@/components/Pages/Market/MarketList";
// Fiat-M / USDT-M 行长按弹出的 "Add to Favorites" 气泡
import AddFavTooltip from "@/components/Pages/Market/MarketList/addFavTooltip";
import { getDynamicFiatMarketCodes } from "@/utils/fiatMarket";

const cacheData = {};
const marketsChartMap = {};
const kInterval = "1h";
const kLimit = 24;
let klineEvents = [];

function isDisplayLevelVisible(mConfig) {
  if (!mConfig) return false;
  if (mConfig.state === "DELISTED") return false;
  return String(mConfig.displayLevel || "").toUpperCase() !== "NONE";
}

const HomeCoinList = () => {
  const t = useTranslation();
  const { fund, market, user } = useStore();
  const [appState] = React.useContext(XtContext);
  // CFD 榜单配置（新接口）
  const [cfdTopConfig, setCfdTopConfig] = useState(null);
  const [showFiatM, setShowFiatM] = useState(false);
  const [fiatCurrency, setFiatCurrency] = useState(null);
  /** Fiat-M 允许展示的法币列表（与 Market 页一致：fiatCurrencyIds + IP + 有余额） */
  const [activeFiatCodes, setActiveFiatCodes] = useState([]);
  const [tradfiBaseCurrencies, setTradfiBaseCurrencies] = useState([]);
  // 一级 tab：Fiat-M / USDT-M
  const [primaryTab, setPrimaryTab] = useState("usdt-m");
  // 首页 Favorites 二级 tab：仅 Crypto / Stocks / Commodities（与 Fiat-M 下二级一致）
  const [favSubTab, setFavSubTab] = useState("crypto");
  // 二级 tab：stocks / commodities / hot / up / down（与 market 页一致，按 plates 过滤）
  const [chooseTab, setChooseTab] = useState("hot");
  const [loading, setLoading] = useState(false);
  const [groupList, setGroupList] = useState([]);
  const [chartList, setChartList] = useState([]);
  const [sortBy, setSortBy] = useState("");
  const chooseTabRef = useRef(chooseTab);
  const primaryTabRef = useRef(primaryTab);
  const sortByRef = useRef(sortBy);
  const groupListRef = useRef(groupList);
  const chartListFrameRef = useRef(null);
  const groupRefreshFrameRef = useRef(null);

  useEffect(() => {
    primaryTabRef.current = primaryTab;
  }, [primaryTab]);
  useEffect(() => {
    chooseTabRef.current = chooseTab;
  }, [chooseTab]);
  useEffect(() => {
    sortByRef.current = sortBy;
  }, [sortBy]);
  useEffect(() => {
    groupListRef.current = groupList;
  }, [groupList]);
  useEffect(() => {
    return () => {
      if (chartListFrameRef.current != null) {
        cancelAnimationFrame(chartListFrameRef.current);
      }
      if (groupRefreshFrameRef.current != null) {
        cancelAnimationFrame(groupRefreshFrameRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // 二级 tab 不再包含 Favourite（已上移为一级 tab）；如果历史状态残留则回落到 hot
    if (chooseTab === "favourite") {
      setChooseTab("hot");
      chooseTabRef.current = "hot";
    }
    // 未登录时若一级停留在 optional，切回默认
    if (user.loginStatus !== 1 && primaryTab === "optional") {
      const fallback = showFiatM ? "fiat-m" : "usdt-m";
      setPrimaryTab(fallback);
      primaryTabRef.current = fallback;
    }
  }, [chooseTab, user.loginStatus, primaryTab, showFiatM]);

  useEffect(() => {
    initMarketGroup();
  }, [appState?.userInfo?.authLevel, appState?.loginStatus, fund?.recommendedCurrencyList, fund?.recommendedCurrencyListMd5]);

  // Spot / Favourite 由嵌入的 MarketList 订阅 tickers；Fiat-M / USDT-M 在此订阅 tickers 并维护 kline@*_cfd
  useEffect(() => {
    const isSpotTab = primaryTab === "spot";
    const usesEmbeddedMarketList = primaryTab === "spot" || primaryTab === "optional";

    // 仅 Spot 不使用 CFD 小图，切走时释放 kline；Favourite 展示 CFD 收藏，保留 kline 订阅
    if (isSpotTab && klineEvents && klineEvents.length > 0) {
      klineEvents.forEach((s) => {
        XtSocketPublic.removeChannel({ event: "kline@" + s + "," + kInterval });
      });
      klineEvents = [];
    }

    if (usesEmbeddedMarketList) {
      return undefined;
    }

    XtSocketPublic.addChannel({ event: "tickers" }, handleWsCallback);
    return () => {
      XtSocketPublic.removeChannel({ event: "tickers" });
    };
  }, [primaryTab, appState?.userInfo?.authLevel, appState?.loginStatus, fund?.recommendedCurrencyList, fund?.recommendedCurrencyListMd5]);

  useEffect(() => {
    return () => {
      if (klineEvents && klineEvents.length > 0) {
        klineEvents.forEach((s) => {
          XtSocketPublic.removeChannel({ event: "kline@" + s + "," + kInterval });
        });
      }
    };
  }, []);

  useEffect(() => {
    if (user.loginStatus === 1) {
      user.getUserMarket();
    }
  }, [user, user.loginStatus]);

  useEffect(() => {
    initChartData();
  }, [groupList]);

  useEffect(() => {
    if (!cacheData.allMarketData || !cacheData.cfdTopConfig) return;
    handleGroupList(primaryTabRef.current, chooseTabRef.current);
  }, [user.userMarket?.join(",")]);

  const buildChartList = (list) => {
    return list.map((g) => marketsChartMap[g.s] || []);
  };

  const setChartListSafely = (list) => {
    if (chartListFrameRef.current != null) return;
    chartListFrameRef.current = requestAnimationFrame(() => {
      chartListFrameRef.current = null;
      const next = buildChartList(groupListRef.current || list || []);
      setChartList((prev) => {
        if (
          Array.isArray(prev) &&
          prev.length === next.length &&
          prev.every((item, index) => item === next[index])
        ) {
          return prev;
        }
        return next;
      });
    });
  };

  const scheduleHandleGroupList = () => {
    if (groupRefreshFrameRef.current != null) return;
    groupRefreshFrameRef.current = requestAnimationFrame(() => {
      groupRefreshFrameRef.current = null;
      handleGroupList(primaryTabRef.current, chooseTabRef.current);
    });
  };

  const handleWsCallback = (res) => {
    if (!Array.isArray(res) || res.length <= 0) return;
    res.forEach((r) => {
      let i = cacheData.allMarketData.findIndex((d) => d.s == r.s);
      if (i >= 0) cacheData.allMarketData[i] = r;
    });
    scheduleHandleGroupList();
  };

  const handleWsKlineCallback = (res) => {
    if (!marketsChartMap[res.s]) marketsChartMap[res.s] = [];
    var list = [];
    var flag = true;
    marketsChartMap[res.s].forEach((item) => {
      if (item.t == res.t) {
        list.push(res);
        flag = false;
      } else list.push(item);
    });
    if (flag) list.unshift(res);
    marketsChartMap[res.s] = list;
    setChartListSafely(groupListRef.current);
  };

  const changeGroupList = (newList) => {
    let addList = [];
    let removeList = [];
    if (Array.isArray(newList) && newList.length > 0) {
      newList.forEach((item) => {
        addList.push(item.s);
      });
    }
    if (Array.isArray(klineEvents) && klineEvents.length > 0) {
      klineEvents.forEach((item) => {
        let i = addList.indexOf(item);
        if (i >= 0) addList.splice(i, 1);
        else removeList.push(item);
      });
    }
    klineEvents = newList.map((item) => {
      return item.s;
    });
    if (removeList.length > 0) {
      removeList.forEach((s) => {
        XtSocketPublic.removeChannel({ event: "kline@" + s + "," + kInterval });
      });
    }
    if (addList.length > 0) {
      addList.forEach((s) => {
        XtSocketPublic.addChannel(
          { event: "kline@" + s + "," + kInterval },
          handleWsKlineCallback
        );
      });
    }
    const currentList = groupListRef.current || [];
    const isSameGroup =
      currentList.length === (newList || []).length &&
      currentList.every((item, index) => {
        const nextItem = newList[index];
        return item?.s === nextItem?.s && item === nextItem;
      });
    if (!isSameGroup) {
      setGroupList(newList);
      groupListRef.current = newList;
    }
    setChartListSafely(newList);
  };

  const initMarketGroup = async () => {
    try {
      setLoading(true);
      cacheData.allMarketData = await get_ticker24h();
      try {
        const userInfo = appState?.userInfo;
        const hasAuthLevel = userInfo && userInfo.authLevel != null;
        const hasFiatIds = userInfo?.fiatCurrencyIds && Array.isArray(userInfo.fiatCurrencyIds) && userInfo.fiatCurrencyIds.length > 0;
        const body =
          hasAuthLevel || hasFiatIds
            ? {
                ...(hasAuthLevel && { authLevel: userInfo.authLevel }),
                ...(hasFiatIds && {
                  fiatCurrencyIds: userInfo.fiatCurrencyIds.map((id) => String(id)),
                }),
              }
            : {};
        const res = await get_cfdMarketTopConfig(body, { isAutoPopError: false });
        const result = res?.result ?? res?.data ?? res;
        if (result) {
          cacheData.cfdTopConfig = result;
          cacheData.tradfiBaseCurrencies = Array.isArray(result.tradfiBaseCurrencies) ? result.tradfiBaseCurrencies : [];
          setFiatCurrency(result.fiatCurrency || null);
          setTradfiBaseCurrencies(cacheData.tradfiBaseCurrencies);
          setCfdTopConfig(result);
        }
      } catch (err) {
        console.warn("get_cfdMarketTopConfig failed, fallback to topConfig", err);
        const fallback = await get_topConfig().catch(() => null);
        const data = fallback?.result ?? fallback?.data ?? fallback;
        if (data) {
          const emptyTabConfig = { limit: 10, topSymbols: [], excludeQuoteCurrencies: [], excludeBaseCurrencies: [], excludeSymbols: [], excludeVolumeLt: null };
          const usdtM = { ...data, tradfi: data.tradfi || emptyTabConfig };
          const defaultTradfi = ["xau", "xag", "wti", "brent", "oil"];
          cacheData.cfdTopConfig = { showFiatM: false, fiatM: null, usdtM, tradfiBaseCurrencies: defaultTradfi };
          cacheData.tradfiBaseCurrencies = defaultTradfi;
          setCfdTopConfig(cacheData.cfdTopConfig);
          setTradfiBaseCurrencies(cacheData.tradfiBaseCurrencies);
        }
      }

      const resolvedCodes = getDynamicFiatMarketCodes();
      cacheData.resolvedFiatCodes = resolvedCodes;
      setShowFiatM(resolvedCodes.length > 0);
      setActiveFiatCodes(resolvedCodes);
      if (resolvedCodes.length > 0) {
        setPrimaryTab("fiat-m");
        primaryTabRef.current = "fiat-m";
      } else {
        setPrimaryTab("usdt-m");
        primaryTabRef.current = "usdt-m";
      }

      const prim = resolvedCodes.length > 0 ? "fiat-m" : "usdt-m";
      handleGroupList(prim, chooseTabRef.current);
    } catch (error) {
      console.log("initMarket-----", error);
    } finally {
      setLoading(false);
    }
  };

  const getVolumeForSort = (item, allMarketData) => {
    let volume = parseFloat(item?.v) || 0;
    const quote = (item?.s?.split("_")[1] || "").toLowerCase();
    if (quote !== "usdt") {
      const pair = allMarketData.find((d) => d.s.toLowerCase() === quote + "_usdt");
      if (pair && pair.c) volume *= parseFloat(pair.c) || 0;
    }
    return volume;
  };

  const sortItems = (items, allMarketData, customSortBy) => {
    if (!customSortBy) return items;
    const ary = customSortBy.split("_");
    const sorted = [...items];
    const strCompare = (a, b) => {
      a = a.toLowerCase();
      b = b.toLowerCase();
      if (a < b) return -1;
      if (a > b) return 1;
      return 0;
    };

    sorted.sort((x, y) => {
      if (ary[0] === "name") {
        return ary[1] === "up" ? strCompare(y.s, x.s) : strCompare(x.s, y.s);
      }
      if (ary[0] === "price") {
        return ary[1] === "up"
          ? (parseFloat(x.c) || 0) - (parseFloat(y.c) || 0)
          : (parseFloat(y.c) || 0) - (parseFloat(x.c) || 0);
      }
      if (ary[0] === "rate") {
        return ary[1] === "up"
          ? (x.cr || x.r || 0) - (y.cr || y.r || 0)
          : (y.cr || y.r || 0) - (x.cr || x.r || 0);
      }
      if (ary[0] === "volume") {
        return ary[1] === "up"
          ? getVolumeForSort(x, allMarketData) - getVolumeForSort(y, allMarketData)
          : getVolumeForSort(y, allMarketData) - getVolumeForSort(x, allMarketData);
      }
      return 0;
    });

    return sorted;
  };

  const handleGroupList = (primary, tab, customSortBy = sortByRef.current) => {
    const allMarketData = cacheData.allMarketData;
    const cfdConfig = cacheData.cfdTopConfig || cfdTopConfig;
    if (!allMarketData || !cfdConfig) return;
    const category = primary === "fiat-m" && cfdConfig.fiatM
      ? cfdConfig.fiatM
      : cfdConfig.usdtM;
    if (!category) return;
    // commodity / stocks 使用接口各自 config（limit、topSymbols、exclude* 等），无则 fallback 到 hot；再按 plates 或接口 base 列表过滤
    const config = category[tab] || category.hot;
    if (!config) return;
    const { marketConfig } = market;
    const commodityBases = (cfdConfig.commodityBaseCurrencies || []).map((c) => (c || "").toLowerCase());
    const stockBases = (cfdConfig.stockBaseCurrencies || []).map((c) => (c || "").toLowerCase());
    const isFavouriteTab = tab === "favourite";
    const favouriteSymbols = Array.isArray(user.userMarket) ? user.userMarket : [];
    const limit = isFavouriteTab ? Number.MAX_SAFE_INTEGER : Math.max(0, config.limit ?? 10);
    const isFiatM = primary === "fiat-m";
    // 与 Market 页一致：Fiat-M 只展示 quote 在 activeFiatCodes 中的币对（fiatCurrencyIds + IP + 有余额）
    const allowedFiat = isFiatM && (Array.isArray(activeFiatCodes) && activeFiatCodes.length > 0 ? activeFiatCodes : cacheData.resolvedFiatCodes || []);
    const allowedFiatSet = allowedFiat.length > 0 ? new Set(allowedFiat.map((c) => (c || "").toUpperCase())) : null;
    const quoteFilter = isFiatM
      ? (quote) => allowedFiatSet != null && allowedFiatSet.has((quote || "").toUpperCase())
      : (quote) => quote.toUpperCase() === "USDT";
    // Fiat-M / USDT-M 仅展示 type 为 "CFD" 的差价合约币对
    const isCfdAndQuoteMatch = (d) => {
      if (!d.s) return false;
      const mConfig = marketConfig?.[d.s];
      if (!isDisplayLevelVisible(mConfig)) return false;
      const symbolType = (mConfig.type || "").toUpperCase();
      if (symbolType !== "CFD") return false;
      const parts = d.s.split("_");
      const quote = (parts[1] || "").toUpperCase();
      return quoteFilter(quote);
    };

    // 二级 tab 过滤：优先 marketConfig.plates（260=大宗商品，261=股票）；无 plates 时用接口 commodityBaseCurrencies/stockBaseCurrencies 与 symbol base 匹配
    const matchPlatesTab = (d) => {
      const base = (d.s || "").split("_")[0]?.toLowerCase() || "";
      const mConfig = marketConfig?.[d.s];
      const plates = Array.isArray(mConfig?.plates) ? mConfig.plates : [];
      const has260 = plates.includes(260);
      const has261 = plates.includes(261);
      const isCommodityByBase = commodityBases.length > 0 && commodityBases.includes(base);
      const isStockByBase = stockBases.length > 0 && stockBases.includes(base);
      if (tab === "commodities") return has260 || isCommodityByBase;
      if (tab === "stocks") return has261 || isStockByBase;
      if (tab === "hot") return !has260 && !has261 && !isCommodityByBase && !isStockByBase;
      return true;
    };

    let topList = [];
    if (!isFavouriteTab && config.topSymbols && config.topSymbols.length > 0) {
      for (const symbol of config.topSymbols) {
        const idx = allMarketData.findIndex((d) => d.s === symbol);
        if (idx >= 0 && isCfdAndQuoteMatch(allMarketData[idx]) && matchPlatesTab(allMarketData[idx])) {
          topList.push(allMarketData[idx]);
          if (topList.length >= limit) break;
        }
      }
    }

    let filterlist = allMarketData.filter((d) => {
      if (!isCfdAndQuoteMatch(d)) return false;
      if (!matchPlatesTab(d)) return false;
      if (isFavouriteTab) {
        if (!favouriteSymbols.length) return false;
        if (!favouriteSymbols.find((s) => s === d.s)) return false;
      }
      const parts = d.s.split("_");
      const base = (parts[0] || "").toLowerCase();
      const quote = (parts[1] || "").toLowerCase();
      if (!isFavouriteTab && topList.some((t) => t.s === d.s)) return false;
      if (!isFavouriteTab && config.excludeSymbols?.includes(d.s)) return false;
      if (!isFavouriteTab && config.excludeBaseCurrencies?.some((c) => (c || "").toLowerCase() === base)) return false;
      if (!isFavouriteTab && config.excludeQuoteCurrencies?.some((c) => (c || "").toLowerCase() === quote)) return false;
      if (!isFavouriteTab && config.excludeVolumeLt != null && config.excludeVolumeLt !== "") {
        const v = parseFloat(d.v);
        const threshold = parseFloat(config.excludeVolumeLt);
        if (!Number.isNaN(threshold) && (Number.isNaN(v) || v < threshold)) return false;
      }
      return true;
    });

    const sortByVolume = (x, y) => getVolumeForSort(y, allMarketData) - getVolumeForSort(x, allMarketData);

    if (tab === "up") filterlist.sort((x, y) => (y.cr || y.r || 0) - (x.cr || x.r || 0));
    else if (tab === "down") filterlist.sort((x, y) => (x.cr || x.r || 0) - (y.cr || y.r || 0));
    else if (tab === "hot" || tab === "commodities" || tab === "stocks") {
      filterlist.sort(sortByVolume);
    } else if (tab === "favourite") {
      filterlist.sort((x, y) => {
        const xi = favouriteSymbols.findIndex((s) => s === x.s);
        const yi = favouriteSymbols.findIndex((s) => s === y.s);
        return xi - yi;
      });
    }

    if (isFavouriteTab) {
      changeGroupList(sortItems(filterlist, allMarketData, customSortBy));
      return;
    }

    const take = Math.max(0, limit - topList.length);
    changeGroupList(sortItems(topList.concat(filterlist.slice(0, take)), allMarketData, customSortBy));
  };

  const initChartData = () => {
    if (!groupList?.length) return;
    groupList.forEach((item) => {
      if (marketsChartMap[item.s] && marketsChartMap[item.s].length > 0) return;
      get_kline({
        params: {
          symbol: item.s,
          interval: kInterval,
          limit: kLimit,
        },
      }).then((data) => {
        marketsChartMap[item.s] = data;
        setChartListSafely(groupListRef.current);
      });
    });
  };

  const handleChangeTab = (tab) => {
    setChooseTab(tab);
    chooseTabRef.current = tab;
    handleGroupList(primaryTabRef.current, tab);
  };

  const handlePrimaryTabChange = (primary) => {
    setPrimaryTab(primary);
    primaryTabRef.current = primary;
    handleGroupList(primary, chooseTabRef.current);
  };

  const clickSort = (attr) => {
    let value = "";
    if (!sortByRef.current) {
      value = attr + "_down";
    } else {
      const ary = sortByRef.current.split("_");
      if (attr !== ary[0]) {
        value = attr + "_down";
      } else if (ary[1] === "down") {
        value = attr + "_up";
      }
    }
    setSortBy(value);
    sortByRef.current = value;
    handleGroupList(primaryTabRef.current, chooseTabRef.current, value);
  };

  const onCoinClick = (item) => {
    window.location.href = getUrl(`/futures/detail/${item.s}`);
  };

  const getCoinIcon = (symbol) => {
    if (!symbol) return require("@/assets/images/common/icon404.png").default;
    const { currencyMap } = fund;
    const coin = symbol.split("_")[0];
    const rawLogo = currencyMap?.[coin]?.["logo"];
    if (!rawLogo) return require("@/assets/images/common/icon404.png").default;
    const domain = document.domain;
    const domain2 = domain.substring(domain.indexOf(".") + 1);
    return !domain.startsWith("xt") ? rawLogo.replace("xt.com", domain2) : rawLogo;
  };

  const getCoinName = (symbol) => {
    if (!symbol) return "";
    return symbol.split("_")[0];
  };

  const getMarketName = (symbol) => {
    if (!symbol) return "";
    return symbol.split("_").length > 1 ? symbol.split("_")[1] : "";
  };

  // Total Balance 选法币时同步 appState.currency -> fund.assist；选 USDT 时 assist 不更新，辅价格沿用 fund.assist
  const rawSelectedKey = (appState?.currency || fund.assist?.key || "usd").toLowerCase();
  const assistCurrencyKey =
    rawSelectedKey === "usdt"
      ? (fund.assist?.key || "usd").toLowerCase()
      : rawSelectedKey;
  const getDisplayCurrencyName = () => {
    if (fund.assist?.key === assistCurrencyKey && fund.assist?.name) return fund.assist.name;
    const cfg = Config.assistConfig.find((item) => item.key === assistCurrencyKey);
    return cfg?.name || assistCurrencyKey.toUpperCase();
  };
  const getPriceAssist = (item) => {
    if (!assistCurrencyKey) return null;
    if ((fund.assist?.key || "").toLowerCase() !== assistCurrencyKey) return null;
    const quoteCurrency = getMarketName(item.s).toLowerCase();
    const result = fund.getCoinValue(item.c, quoteCurrency);
    if (result == null || result === "") return null;
    return `${Big(result || 0).toFixedCy()} ${getDisplayCurrencyName()}`;
  };

  const getVol = (item) => {
    if (!item?.v) return "--";
    let volume = +item.v || 0;

    if (appState?.lang === "zh-CN") {
      if (volume > 1e8) return Big(volume / 1e8).toFixedMaxCy(4) + "亿";
      if (volume > 1e4) return Big(volume / 1e4).toFixedMaxCy(2) + "万";
      return Big(volume).toFixedCy();
    }

    if (volume > 1e8) return Big(volume / 1e6).toFixedMaxCy(4) + "M";
    if (volume > 1e4) return Big(volume / 1e3).toFixedMaxCy(2) + "K";
    return Big(volume).toFixedCy();
  };

  const formatPrice = (item) => {
    const { s: marketName, c: latestPrice } = item;
    if (latestPrice == null || isNaN(+latestPrice)) return "0";
    const mConfig = market.marketConfig[marketName];
    return Big(latestPrice).toFixedCy(mConfig && mConfig.buyPoint ? mConfig.buyPoint : 2);
  };

  const fontClass = (rate) => {
    return !isNaN(+rate) && +rate != 0
      ? +rate > 0
        ? styles["up-color"]
        : styles["down-color"]
      : styles["up-color"];
  };

  const fontClassGlobal = (rate) => {
    return !isNaN(+rate) && +rate != 0 ? (+rate > 0 ? "up-color" : "down-color") : "down-color";
  };

  const formatRate = (rate) => {
    return showRate(rate * 100);
  };

  const getMaketOpenTimeTs = (symbol) => {
    const m = market.marketConfig?.[symbol];
    return m && m.stateTime != null ? +m.stateTime : 0;
  };

  // 一级 tab 为 Favourite / Spot 时：直接复用 /market 的 MarketList，保证行为与 /market 完全一致
  const isFavouritePrimary = primaryTab === "optional";
  const isSpotPrimary = primaryTab === "spot";

  // Fiat-M / USDT-M 行长按 → 弹出 "Add to Favorites" 气泡
  const [addFavTip, setAddFavTip] = useState(null); // { x, y, symbol } | null
  const longPressTimerRef = useRef(null);
  const longPressTriggeredRef = useRef(false);
  const LONG_PRESS_DURATION = 500;

  const clampToViewport = (x, y, w, h) => {
    const vw = typeof window !== "undefined" ? window.innerWidth : w;
    const vh = typeof window !== "undefined" ? window.innerHeight : h;
    return {
      x: Math.max(8, Math.min(x, vw - w - 8)),
      y: Math.max(8, Math.min(y, vh - h - 8)),
    };
  };

  const popAddFavTip = (clientX, clientY, symbol) => {
    const TIP_W = 110;
    const TIP_H = 37;
    const { x, y } = clampToViewport(clientX - TIP_W / 2, clientY - TIP_H - 8, TIP_W, TIP_H);
    setAddFavTip({ x, y, symbol });
  };

  // 行内的可点击元素（如 ⭐ 收藏按钮）不参与长按，避免短按收藏时误触 Add/Remove 气泡。
  // 不匹配 [role='button']：避免未来给行本身挂该角色时误判整行
  const isInteractiveTarget = (target) => {
    if (!target || typeof target.closest !== "function") return false;
    return !!target.closest("button, a, input");
  };

  const startRowLongPress = (e, symbol) => {
    if (isInteractiveTarget(e.target)) return;
    longPressTriggeredRef.current = false;
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    longPressTimerRef.current = setTimeout(() => {
      longPressTriggeredRef.current = true;
      popAddFavTip(clientX, clientY, symbol);
    }, LONG_PRESS_DURATION);
  };

  const cancelRowLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleRowContextMenu = (e, symbol) => {
    if (isInteractiveTarget(e.target)) return;
    e.preventDefault();
    longPressTriggeredRef.current = true;
    popAddFavTip(e.clientX, e.clientY, symbol);
  };

  const closeAddFavTip = () => setAddFavTip(null);

  return (
    <div className={styles["coin-list"]}>
      <div className={styles["coin-list__container"]}>
        {/* 一级 tab：Favourite（登录后） / Fiat-M（仅 showFiatM 时） / USDT-M */}
        <div className={styles["coin-list__primary-tabs"]}>
          {user.loginStatus === 1 && (
            <div
              onClick={() => handlePrimaryTabChange("optional")}
              className={`${styles["coin-list__primary-tab"]} ${
                primaryTab === "optional" ? styles["coin-list__primary-tab--active"] : ""
              }`}
            >
              {t("trade.favorites")}
            </div>
          )}
          {showFiatM && (
            <div
              onClick={() => handlePrimaryTabChange("fiat-m")}
              className={`${styles["coin-list__primary-tab"]} ${
                primaryTab === "fiat-m" ? styles["coin-list__primary-tab--active"] : ""
              }`}
            >
              {t("market.tabFiatM")}
            </div>
          )}
          <div
            onClick={() => handlePrimaryTabChange("usdt-m")}
            className={`${styles["coin-list__primary-tab"]} ${
              primaryTab === "usdt-m" ? styles["coin-list__primary-tab--active"] : ""
            }`}
          >
            {t("market.tabUsdtM")}
          </div>
          <div
            onClick={() => handlePrimaryTabChange("spot")}
            className={`${styles["coin-list__primary-tab"]} ${
              primaryTab === "spot" ? styles["coin-list__primary-tab--active"] : ""
            }`}
          >
            {t("home.spot")}
          </div>
        </div>

        {isFavouritePrimary ? (
          <>
            <div className={styles["coin-list__tabs"]}>
              <div
                onClick={() => setFavSubTab("crypto")}
                className={`${styles["coin-list__tab"]} ${
                  favSubTab === "crypto" ? styles["coin-list__tab--active"] : ""
                }`}
              >
                {t("market.subTabCrypto")}
              </div>
              <div
                onClick={() => setFavSubTab("stocks")}
                className={`${styles["coin-list__tab"]} ${
                  favSubTab === "stocks" ? styles["coin-list__tab--active"] : ""
                }`}
              >
                {t("market.subTabStocks")}
              </div>
              <div
                onClick={() => setFavSubTab("commodities")}
                className={`${styles["coin-list__tab"]} ${
                  favSubTab === "commodities" ? styles["coin-list__tab--active"] : ""
                }`}
              >
                {t("market.subTabCommodities")}
              </div>
            </div>
            {/* 复用 /market 的 Favourite：排序、空态推荐、长按菜单、编辑入口；首页去掉搜索框，按 Crypto/Stocks/Commodities 过滤 */}
            <div className={styles["coin-list__fav-mount"]}>
              <MarketList
                marketType="optional"
                activeFiatCodes={activeFiatCodes}
                subTab={favSubTab}
                showFiatTab={showFiatM}
                hideSearchBar
                onGoToCryptoTab={(preferFiat) => {
                  handlePrimaryTabChange(preferFiat && showFiatM ? "fiat-m" : "usdt-m");
                }}
              />
            </div>
          </>
        ) : isSpotPrimary ? (
          <div className={styles["coin-list__fav-mount"]}>
            <MarketList
              marketType="spot"
              hideSearchBar
              activeFiatCodes={activeFiatCodes}
              showFiatTab={showFiatM}
            />
          </div>
        ) : (
          <>
        {/* 二级 tab：Crypto / 股票 / 大宗商品 / Top Gainers / Top Losers（与 /market 下 Fiat-M·USDT-M 二级 Tab 文案一致：market.subTabCrypto） */}
        <div className={styles["coin-list__tabs"]}>
          <div
            onClick={() => handleChangeTab("hot")}
            className={`${styles["coin-list__tab"]} ${
              chooseTab === "hot" ? styles["coin-list__tab--active"] : ""
            }`}
          >
            {t("market.subTabCrypto")}
          </div>
          <div
            onClick={() => handleChangeTab("stocks")}
            className={`${styles["coin-list__tab"]} ${
              chooseTab === "stocks" ? styles["coin-list__tab--active"] : ""
            }`}
          >
            {t("market.subTabStocks")}
          </div>
          <div
            onClick={() => handleChangeTab("commodities")}
            className={`${styles["coin-list__tab"]} ${
              chooseTab === "commodities" ? styles["coin-list__tab--active"] : ""
            }`}
          >
            {t("market.subTabCommodities")}
          </div>
          <div
            onClick={() => handleChangeTab("up")}
            className={`${styles["coin-list__tab"]} ${
              chooseTab === "up" ? styles["coin-list__tab--active"] : ""
            }`}
          >
            {t("home.topGainers")}
          </div>
          <div
            onClick={() => handleChangeTab("down")}
            className={`${styles["coin-list__tab"]} ${
              chooseTab === "down" ? styles["coin-list__tab--active"] : ""
            }`}
          >
            {t("home.topLosers")}
          </div>
        </div>

        <div className={styles["coin-list__content"]}>
          <div className={styles["coin-list__header"]}>
            <button
              type="button"
              className={`${styles["coin-list__header-button"]} ${
                sortBy === "name_up" ? styles["coin-list__header-button--up"] : sortBy === "name_down" ? styles["coin-list__header-button--down"] : ""
              }`}
              onClick={() => clickSort("name")}
            >
              <span>{t("home.Name")} / {t("trade.volume")}</span>
              <span className={styles["coin-list__sort-icon"]} aria-hidden="true" />
            </button>
            <div className={styles["coin-list__header-ai"]} aria-hidden="true" />
            <button
              type="button"
              className={`${styles["coin-list__header-button"]} ${
                sortBy === "price_up" ? styles["coin-list__header-button--up"] : sortBy === "price_down" ? styles["coin-list__header-button--down"] : ""
              }`}
              onClick={() => clickSort("price")}
            >
              <span>{t("home.LastPrice")}</span>
              <span className={styles["coin-list__sort-icon"]} aria-hidden="true" />
            </button>
            <button
              type="button"
              className={`${styles["coin-list__header-button"]} ${
                sortBy === "rate_up" ? styles["coin-list__header-button--up"] : sortBy === "rate_down" ? styles["coin-list__header-button--down"] : ""
              }`}
              onClick={() => clickSort("rate")}
            >
              <span>{t("trade.change")}</span>
              <span className={styles["coin-list__sort-icon"]} aria-hidden="true" />
            </button>
          </div>

          <div className={styles["coin-list__list"]}>
            {loading ? (
              <div className={styles["coin-list__loading"]}>{t("common.loading")}</div>
            ) : chooseTab === "favourite" && groupList.length === 0 ? (
              <div className={styles["coin-list__empty"]}>
                <div className={styles["coin-list__empty-text"]}>
                  {t("trade.noFavorites")}
                </div>
              </div>
            ) : (
              groupList.map((item, index) => {
                const priceAssist = getPriceAssist(item);
                return (
                <div
                  key={item.s}
                  className={styles["coin-list__item"]}
                  onClick={() => {
                    if (longPressTriggeredRef.current) {
                      longPressTriggeredRef.current = false;
                      return;
                    }
                    onCoinClick(item);
                  }}
                  onMouseDown={(e) => startRowLongPress(e, item.s)}
                  onMouseUp={cancelRowLongPress}
                  onMouseLeave={cancelRowLongPress}
                  onTouchStart={(e) => startRowLongPress(e, item.s)}
                  onTouchEnd={cancelRowLongPress}
                  onTouchMove={cancelRowLongPress}
                  onTouchCancel={cancelRowLongPress}
                  onContextMenu={(e) => handleRowContextMenu(e, item.s)}
                >
                  <div className={styles["coin-list__name"]}>
                    <div className={styles["coin-list__name-wrapper"]}>
                      <div className={styles["coin-list__name-text"]}>
                        <span className={styles["coin-list__symbol"]}>
                          <span className={styles["coin-list__coin"]}>
                            {getCoinName(item.s).toUpperCase()}
                          </span>
                          <span className={styles["coin-list__market"]}>
                            / {getMarketName(item.s).toUpperCase()}
                          </span>
                        </span>
                      </div>
                      <div className={styles["coin-list__volume"]}>
                        <Star marketType={primaryTab} symbol={item.s} />
                        <span>{getVol(item)}</span>
                      </div>
                    </div>
                  </div>
                  <div className={styles["coin-list__ai"]}>
                    {isAiselectEligibleSymbol(item.s, market.marketConfig) ? (
                      <span
                        className={styles["coin-list__ai-select-wrap"]}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <AiSelectNavButton
                          symbol={item.s}
                          aria-label={t("header.aicopy")}
                        />
                      </span>
                    ) : null}
                  </div>
                  <div className={styles["coin-list__price"]}>
                    <div className={styles["coin-list__price-main"]}>{formatPrice(item)}</div>
                    {priceAssist ? (
                      <div className={styles["coin-list__price-assist"]}>{priceAssist}</div>
                    ) : null}
                  </div>
                  <div className={styles["coin-list__change"]}>
                    <div
                      className={`${styles["coin-list__change-badge"]} ${fontClass(
                        item.cr || item.r
                      )}`}
                    >
                      {formatRate(item.cr || item.r)}
                    </div>
                  </div>
                </div>
              );
              })
            )}
          </div>
        </div>
          </>
        )}
      </div>
      {addFavTip && (
        <AddFavTooltip
          position={addFavTip}
          alreadyFav={Array.isArray(user.userMarket) && user.userMarket.includes(addFavTip.symbol)}
          onClose={closeAddFavTip}
          // alreadyFav=true 时执行移除，否则添加；确保 Remove from Favorites 后 Star 图标立即变灰
          onConfirm={(alreadyFav) => {
            if (alreadyFav) void user.removeUserMarket(addFavTip.symbol);
            else void user.addUserMarket(addFavTip.symbol);
          }}
        />
      )}
    </div>
  );
};

export default observer(HomeCoinList);
